import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, resetPassword } from 'aws-amplify/auth';
import '../styles/design-system.css';

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('🔍 Login form submitted');
    console.log('Email:', email);
    console.log('Password length:', password.length);
    
    setError('');
    setLoading(true);
    
    if (!email.trim()) {
      console.log('❌ Email is empty');
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }
    
    if (!password.trim()) {
      console.log('❌ Password is empty');
      setError('Please enter your password.');
      setLoading(false);
      return;
    }
    
    try {
      console.log('🚀 Attempting login with email:', email);
      const signInResult = await signIn({ 
        username: email.trim(), 
        password: password 
      });
      console.log('✅ Login successful:', signInResult);
      
      if (onLogin) {
        console.log('📞 Calling onLogin callback');
        onLogin({ username: email });
      }
      
      console.log('🔄 Navigating to profile page');
      navigate('/profile');
    } catch (err) {
      console.error('❌ Login error details:', {
        message: err.message,
        name: err.name,
        code: err.code,
        fullError: err
      });
      
      if (err.message.includes('UserNotFoundException')) {
        setError('No account found with this email address. Please check your email or sign up.');
      } else if (err.message.includes('NotAuthorizedException')) {
        setError('Incorrect email or password. Please try again.');
      } else if (err.message.includes('UserNotConfirmedException')) {
        setError('Please check your email and verify your account before signing in.');
      } else if (err.message.includes('Username') && err.message.includes('required')) {
        setError('Please enter your email address.');
      } else if (err.message.includes('Password') && err.message.includes('required')) {
        setError('Please enter your password.');
      } else {
        setError('Login failed. Please check your email and password and try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (!resetEmail.trim()) {
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }
    
    try {
      await resetPassword(resetEmail);
      setResetSent(true);
      setResetEmail('');
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.message.includes('UserNotFoundException')) {
        setError('No account found with this email address.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="container-sm">
        <div className="card">
          <div className="card-header text-center">
            <div className="flex items-center justify-center mb-6">
              <img 
                src="/gradedwash-logo.svg" 
                alt="GradedWash Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }} className="text-2xl font-bold text-primary">
                GradedWash
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Sign in to your GradedWash account</p>
          </div>
          
          <div className="card-body">
            {error && (
              <div className="alert alert-error mb-6">
                {error}
              </div>
            )}
            
            {resetSent && (
              <div className="alert alert-success mb-6">
                Password reset email sent! Please check your inbox.
              </div>
            )}
            
            {!showReset ? (
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Enter your password"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary w-full"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowReset(true)}
                    className="text-primary hover:text-primary-dark text-sm"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleReset} className="space-y-6">
                <div className="form-group">
                  <label htmlFor="resetEmail" className="form-label">
                    Email Address
                  </label>
                  <input
                    id="resetEmail"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="form-input"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary w-full"
                >
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowReset(false)}
                    className="text-primary hover:text-primary-dark text-sm"
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            )}
            
            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-primary hover:text-primary-dark font-medium">
                  Sign up here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 